import React from 'react';
import { Box, Drawer } from '@mui/material';
import { useResponsive } from '../hooks/useResponsive';
import { useThemeMode } from '@vortx/shared-ui';
import DrawerEdgeTab from './DrawerEdgeTab';

/**
 * Wrapper that renders children as sidebar on desktop, drawer on narrow screens
 *
 * @param {string} anchor - 'left' or 'right'
 * @param {boolean} open - Drawer open state (only used below 1200px)
 * @param {function} onClose - Close handler
 * @param {function} onToggle - Toggle handler for edge tab
 * @param {object} desktopSx - Styles for desktop sidebar container
 * @param {ReactNode} children - Sidebar content
 */
const ResponsiveDrawer = ({
    anchor,
    open,
    onClose,
    onToggle,
    desktopSx = {},
    children
}) => {
    const { isLargeDesktop, isMobile } = useResponsive();
    const { isRetro } = useThemeMode();

    // Height calculation: account for status bar in retro mode
    const headerHeight = 64;
    const statusBarHeight = isRetro ? 50 : 0;
    const topOffset = `${headerHeight}px`;
    const heightCalc = `calc(100% - ${headerHeight + statusBarHeight}px)`;

    // Desktop: render normally
    if (isLargeDesktop) {
        return (
            <Box sx={desktopSx}>
                {children}
            </Box>
        );
    }

    // Narrow screens: drawer width
    // Tablets (600-1199px): fixed width
    // Phones (<600px): 80% width
    const drawerWidth = isMobile ? '80%' : (anchor === 'left' ? 280 : 300);

    return (
        <>
            {/* Edge tab - always visible on narrow screens */}
            <DrawerEdgeTab
                anchor={anchor}
                open={open}
                onClick={onToggle}
            />

            {/* MUI Drawer */}
            <Drawer
                anchor={anchor}
                open={open}
                onClose={onClose}
                ModalProps={{
                    keepMounted: true, // Better mobile performance
                }}
                PaperProps={{
                    sx: {
                        width: drawerWidth,
                        top: topOffset,
                        height: heightCalc,
                        // Don't spread desktopSx here - it contains display conditions meant for desktop Box only
                        background: isRetro ? 'rgba(0, 0, 0, 0.95)' : 'background.paper',
                        borderRight: anchor === 'left' ? '1px solid rgba(63, 106, 100, 0.3)' : undefined,
                        borderLeft: anchor === 'right' ? '1px solid rgba(63, 106, 100, 0.3)' : undefined,
                    },
                }}
                sx={{
                    '& .MuiBackdrop-root': {
                        top: topOffset,
                        height: heightCalc,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    },
                }}
            >
                {children}
            </Drawer>
        </>
    );
};

export default ResponsiveDrawer;
